<script setup lang="ts">
import {
  onMounted,
  useStorage,
  ref,
  useI18n,
  useRuntimeConfig,
  useToast,
  watch,
  h,
} from '#imports';

import debounce from 'lodash.debounce';
import { Loader } from '@googlemaps/js-api-loader';
import { render } from 'vue';
import type { Tables } from '@/types/database.types.ts';

import MPAddSpot from '@/components/MPAddSpot/MPAddSpot.vue';

const { formatedImageUrl } = useStorage();
const emit = defineEmits(['markerClick', 'mapClick']);
const props = defineProps({
  zoom: {
    type: Number,
    default: 10,
  },
  locations: {
    type: Array as PropType<Tables<'spots'>[]>,
    required: true,
  },
  search: {
    type: Boolean,
    default: false,
    required: false,
  },
  fitBounds: {
    type: Boolean,
    default: true,
  },
  gestureHandling: {
    type: String,
    default: 'greedy',
  },
});

const { t } = useI18n();
const { showError, logger } = useToast();

const config = useRuntimeConfig();
const loader = new Loader({
  apiKey: useRuntimeConfig().public.googleMapsKey,
  version: 'weekly',
});

const searchTerm = ref<string>('');
const searchResults = ref<google.maps.places.PlaceResult[]>([]);
const searchPlaces = async (
  term: string,
): Promise<google.maps.places.PlaceResult[]> => {
  const google = await loader.importLibrary('places');
  const service = new google.PlacesService(document.createElement('div'));
  const request = {
    query: term,
    fields: ['name', 'geometry', 'formatted_address', 'website', 'place_id'],
  };
  return new Promise((resolve) => {
    service.textSearch(request, (results, status) => {
      if (status === google.PlacesServiceStatus.OK && results) {
        resolve(results);
      } else if (status === google.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([])
      } else {
        console.error(t('messages.maps.search-error'), status);
        resolve([]);
      }
    });
  });
};

watch(
  searchTerm,
  debounce(async (newTerm: string) => {
    try {
      searchResults.value = await searchPlaces(newTerm);
    } catch (e) {
      logger('could not find serach term result', e);
      searchResults.value = [];
    }
  }, 250),
);

const centerOnSpot = (spotId) => {
  if (!map) return;
  const spot = props.locations.find((location) => location.id === spotId);
  if (spot?.lat && spot.lon) {
    const currentZoom = map.getZoom();
    const targetPosition = { lat: spot.lat, lng: spot.lon };

    map.setZoom(currentZoom - 0.25);

    setTimeout(() => {
      map.panTo(targetPosition);
      setTimeout(() => {
        map.setZoom(15);
      }, 500);
    }, 250);
  }
};

// Expose the method
defineExpose({ centerOnSpot });

const setPlaceDetails = async (spot: google.maps.places.PlaceResult) => {
  const placeId = spot.place_id;
  const spotDetails = await getPlaceDetails(placeId);
  emit('mapClick', spotDetails);
  searchTerm.value = '';
};

let map: google.maps.Map | null = null;
const mapElement = ref<HTMLElement | null>(null);

const addMarkersToMap = (
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
  PinElement: typeof google.maps.marker.PinElement
) => {
  if (!map) return;
  for (const location of props.locations) {
    if (location.lat && location.lon) {
      const position = { lat: location.lat, lng: location.lon };
      const glyphImg = document.createElement('img');
      glyphImg.src = formatedImageUrl(location.cover_image_url, 32, 32);
      glyphImg.style.width = '32px';
      glyphImg.style.height = '32px';
      glyphImg.style.objectFit = 'cover';
      glyphImg.style.objectPosition = 'center';
      glyphImg.style.borderRadius = '50%';
      // glyphImg.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';

      glyphImg.addEventListener("mouseover", (event) => {
        alert('hover')
        glyphImg.style.transform = 'scale(1.1)';
      });

      const glyphSvgPinElement = new PinElement({
          glyph: glyphImg,
          glyphColor: 'white',
          background: 'white',
          borderColor: '#cad4e0',
          scale: 1.5
      });
      try {
        const marker = new AdvancedMarkerElement({
          map,
          position,
          content: glyphSvgPinElement.element,
          title: location.name || 'Unnamed location',
        });
        glyphSvgPinElement.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
        glyphImg.style.border = '1px solid #ddd';
        if (!props.search) {
          marker.addListener('click', () => {
            emit('markerClick', location.id);
          });
        } else {
          marker.addListener('click', () => {
            emit('markerClick', location);
          });
        }
      } catch (error) {
        showError('Error adding marker to map', error);
      }
    }
  }
};

const createInfoWindow = (
  position: google.maps.LatLngLiteral,
  placeDetails: google.maps.places.PlaceResult,
) => {
  const content = document.createElement('div');
  const mpAddSpotComponent = h(MPAddSpot, { position, placeDetails });
  render(mpAddSpotComponent, content);

  const infoWindow = new google.maps.InfoWindow({
    content,
    position,
  });

  return infoWindow;
};

const createMarker = (
  position: google.maps.LatLngLiteral,
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
  PinElement: typeof google.maps.marker.PinElement,
) => {
  if (!map) return null;

  return new AdvancedMarkerElement({
    map,
    position,
    title: 'New Location',
  });
};

const getPlaceDetails = async (
  placeId: string,
): Promise<google.maps.places.PlaceResult> => {
  const loader = new Loader({
    apiKey: useRuntimeConfig().public.googleMapsKey,
    version: 'weekly',
  });
  const google = await loader.importLibrary('places');
  const service = new google.PlacesService(document.createElement('div'));
  const request = {
    placeId,
    fields: ['name', 'geometry', 'formatted_address', 'website', 'place_id'],
  };
  return new Promise((resolve, reject) => {
    service.getDetails(request, (place, status) => {
      if (status === google.PlacesServiceStatus.OK && place) {
        resolve(place);
      } else {
        reject(new Error(t('messages.maps.place-details-error')));
      }
    });
  });
};

let currentInfoWindow: google.maps.InfoWindow | null = null;

const handleAddLocation = async (
  position: google.maps.LatLngLiteral,
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
  PinElement: typeof google.maps.marker.PinElement,
) => {
  const geocoder = new google.maps.Geocoder();
  try {
    const result = await geocoder.geocode({ location: position });
    if (result.results[0]) {
      // get place id from result
      const placeId = result.results[0].place_id;
      const placeDetails = await getPlaceDetails(placeId);
      emit('mapClick', placeDetails);
    } else {
      emit('mapClick', position);
    }
  } catch (error) {
    console.error('Geocoding failed:', error);
    emit('mapClick', position);
  }

  // Close the info window after emitting
  if (currentInfoWindow) {
    // Create new marker
    createMarker(position, AdvancedMarkerElement, PinElement);
    currentInfoWindow.close();
  }
};

const initializeMap = async () => {
  const loader = new Loader({
    apiKey: config.public.googleMapsKey as string,
    version: 'weekly',
  });

  try {
    // biome-ignore lint/suspicious/noShadowRestrictedNames:
    const { Map } = (await loader.importLibrary(
      'maps',
    )) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement, PinElement } = (await loader.importLibrary(
      'marker',
    )) as google.maps.MarkerLibrary;

    if (!mapElement.value) {
      showError('Map element not found');
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    for (const location of props.locations) {
      if (location.lat && location.lon) {
        bounds.extend({ lat: location.lat, lng: location.lon });
      }
    }

    map = new Map(mapElement.value, {
      mapId: 'MAPPR',
      zoom: props.zoom,
      center: bounds.getCenter(),
      disableDefaultUI: true,
      gestureHandling: props.gestureHandling,
      panControl: true,
      zoomControl: false,
    });

    if (props.search) {
      map.addListener('click', async (event: google.maps.MapMouseEvent) => {
        const clickedLatLng = event.latLng;
        if (clickedLatLng && AdvancedMarkerElement) {
          // Remove previous info window if exists

          if (currentInfoWindow) {
            currentInfoWindow.close();
          }

          // Resolve place details
          const position = {
            lat: clickedLatLng.lat(),
            lng: clickedLatLng.lng(),
          };
          const geocoder = new google.maps.Geocoder();
          const placeId = await geocoder.geocode({ location: clickedLatLng });
          const placeDetails = await getPlaceDetails(
            placeId.results[0].place_id,
          );

          // Create and open info window
          currentInfoWindow = createInfoWindow(position, placeDetails);
          currentInfoWindow.open(map);

          // Add event listener to the "Add Location" button
          setTimeout(() => {
            const addLocationBtn = document.getElementById('addLocationBtn');
            if (addLocationBtn) {
              addLocationBtn.addEventListener('click', () =>
                handleAddLocation(position, AdvancedMarkerElement),
              );
            }
          }, 0);
        }
      });
    }

    // Use a callback to ensure the map is ready
    map.addListener('idle', () => {
      setTimeout(() => addMarkersToMap(AdvancedMarkerElement, PinElement), 100);
    });

    if (props.fitBounds) {
      map.fitBounds(bounds);
    }
  } catch (error) {
    showError('Error loading Google Maps', error);
  }
};

onMounted(() => {
  initializeMap();
});

watch(
  () => props.locations,
  () => {
    initializeMap();
  },
);

</script>
<template>
  <div v-if="props.search">
    <MPAutoComplete
      id="search"
      v-model="searchTerm"
      name="search"
      label="Search for a spot"
      :suggestions="searchResults"
      option-label="name"
      @complete="searchPlaces(searchTerm)"
      @item-select="setPlaceDetails"
    >
      <template #item="slotProps">
        <div class="search-result">
          <span><img v-if="slotProps.item.icon" :src="slotProps.item.icon" width="13px" :style="{ opacity: '0.5' }" >&nbsp;{{ slotProps.item.name }}</span>
          <small>{{ slotProps.item.formatted_address }}</small>
        </div>
      </template>
    </MPAutoComplete>
    <div class="help-text">
      Enter a location or address; search results provided by Google Maps
    </div>
  </div>
  <div ref="mapElement" class="map-container" />
</template>
<style>
main {
  width: 100%;
  height: 100%;
}
.map-container {
  grid-column: 1/-1;
  width: 100%;
  height: 100%;
  display: block;
  flex-grow: 1;
}
.search-result {
  display: grid;
  align-items: center;
  & small {
    color: var(--color-slate-500);
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
}
</style>
